import type { FC } from "react"
import { useState, useEffect, useMemo } from "react"
import { captureMessage } from "@sentry/nextjs"
import type { FieldProps } from "formik"
import { Field, Formik, useFormikContext } from "formik"
import {
  Badge,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Spacer,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useRadioGroup,
} from "@chakra-ui/react"
import * as Yup from "yup"
import capitalize from "lodash.capitalize"
import { BigNumber } from "ethers"
import Link from "next/link"
import type { FetchSignerResult, Signer } from "@wagmi/core"
import { BN } from "bn.js"
import * as Sentry from "@sentry/nextjs"

import type { SupportWithLabel } from "common/components/voting/VotesCastContainer"
import type {
  Delegation,
  Maybe,
  OnchainVote,
  Vote,
  Chain,
  ProposalVoteModalQuery,
} from "query/graphql"
import {
  AccountType,
  GovernorType,
  VoteType,
  VotesSortBy,
  useProposalCastVotesQuery,
  useProposalVoteModalQuery,
} from "query/graphql"
import VoteCastingOption from "voting/components/VoteCastingOption"
import type { VoteStyle } from "common/helpers/voteStyle"
import { getVoteStyles } from "common/helpers/voteStyle"
import type { VotingModalSupportFormValues } from "common/types/voting"
import { useAddressEligibleFreeVoting } from "voting/hooks/useAddressElligibleFreeVoting"
import VotingEligibleMessage from "voting/components/VotingEligibleMessage"
import chains from "web3/constants/chains"
import GenericNetworkIcon from "web3/components/icons/GenericChainIcon"
import CircleDashed from "ui/components/icons/CircleDashed"
import { getWeightLabel } from "common/helpers/bignumber"
import {
  accountIdToChainId,
  chainIdToChainReference,
} from "web3/helpers/transformers"
import { CircleCheckAlt } from "ui/components/icons/CircleCheckAlt"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"
import { useChainReference } from "web3/hooks/useChainReference"
import { useCastVote } from "voting/hooks/useCastVote"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useCastVoteBySig } from "voting/hooks/useCastVoteBySig"
import { useSignerStore } from "web3/providers/SignerProvider"
import Web3Button from "web3/components/Web3Button"
import { TransactionState } from "web3/types/transaction"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import ConfirmDialog from "common/components/ConfirmDialog"
import { useAccountBalanceCheck } from "web3/hooks/useAccountBalanceCheck"
import { useModals } from "common/hooks/useModals"
import { useStakeAccount } from "web3/solana/wormhole-staking/StakeAccountProvider"
import StakeAccountIntercept from "web3/solana/wormhole-staking/StakeAccountIntercept"
import { TransactionStatus } from "web3/solana/wormhole-staking/useMultigovStaking"
import useIsContract from "web3/hooks/useIsContract"
import { getSolanaChain } from "web3/helpers/chains"
import { toFormattedString, Severity } from "common/components/ErrorBoundary"
import { isSolanaAddress } from "web3/helpers/address"

type VotingModalSupportFormProps = {
  isLoading: boolean
  supports?: VoteType[] | null
  governorType?: GovernorType
  governanceId?: string
  proposalId?: string
  onchainId?: string
  voterAddress?: string // `0x${string}`
  isProposalVotingPowerLoading: boolean
  votableChains: Delegation[] | undefined
  proposal: ProposalVoteModalQuery["proposal"]
  isMultiChain?: boolean
  onSubmit: (values: VotingModalSupportFormValues) => void
}

const initialValues: VotingModalSupportFormValues = {
  support: "",
  reason: "",
  hasFreeVoting: false,
}

const validationSchema = Yup.object().shape({
  support: Yup.string().required("Your vote is required."),
})

const VotingModalSupportForm: FC<VotingModalSupportFormProps> = ({
  isLoading,
  governorType,
  onSubmit,
  governanceId,
  proposalId,
  onchainId,
  voterAddress,
  isProposalVotingPowerLoading,
  isMultiChain,
  proposal,
  votableChains,
}) => {
  const chainReference = useChainReference()
  const { confirmDialogModal } = useModals()
  const { hasBalance, isBalanceRequired, skipBalanceCheck } =
    useAccountBalanceCheck({
      chainReference,
    })

  const handleOnSubmit = (values: VotingModalSupportFormValues) => {
    if (!isMultiChain && !hasBalance && isBalanceRequired) {
      confirmDialogModal.onOpen()

      return
    }

    onSubmit(values)
  }

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ errors, isValid, values, handleSubmit }) => (
        <>
          <form onSubmit={handleSubmit}>
            <Stack px={6} py={4} spacing={8}>
              <Field name="support">
                {() => (
                  <FormControl>
                    <FormLabel htmlFor="support">
                      <Text color="gray.600" textStyle="label">
                        Vote
                      </Text>
                    </FormLabel>
                    <SupportRadioGroup governorType={governorType} />
                    <FormErrorMessage>{errors.support}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="reason">
                {({ field }: FieldProps) => (
                  <FormControl>
                    <FormLabel htmlFor="reason">
                      <Text color="gray.600" textStyle="label">
                        Add comment
                      </Text>
                    </FormLabel>
                    <Textarea
                      _placeholder={{
                        textStyle: "body.regular.md",
                      }}
                      color="gray.500"
                      isDisabled={values.hasFreeVoting}
                      placeholder="Why are you voting this way?"
                      textStyle="body.regular.md"
                      {...field}
                    />
                  </FormControl>
                )}
              </Field>

              <FreeVotingOption
                governanceId={governanceId}
                proposalId={proposalId}
                voterAddress={voterAddress}
              />

              {isMultiChain &&
              votableChains &&
              votableChains?.length > 0 &&
              !isProposalVotingPowerLoading ? (
                <Stack>
                  <Text color="gray.600" textStyle="label">
                    Submit vote
                  </Text>

                  <MultiChainVote
                    onchainId={onchainId}
                    proposal={proposal}
                    proposalId={proposalId}
                    votableChains={votableChains}
                    voter={voterAddress}
                  />
                </Stack>
              ) : null}

              {!isMultiChain ? (
                <Button
                  isDisabled={!isValid}
                  isLoading={isLoading}
                  type="submit"
                  variant="primary"
                  w="full"
                >
                  Submit
                </Button>
              ) : null}
            </Stack>
          </form>

          <ConfirmDialog
            body="You don't have tokens to pay gas fees. Are you sure you want to continue?"
            confirmLabelBtn="Yes, continue"
            onConfirm={() => {
              confirmDialogModal.onClose()
              skipBalanceCheck()
              onSubmit(values)
            }}
          />
        </>
      )}
    </Formik>
  )
}

export default VotingModalSupportForm

type FreeVotingOptionProps = {
  governanceId?: string
  voterAddress?: string // `0x${string}`
  proposalId?: string
}
const FreeVotingOption: FC<FreeVotingOptionProps> = ({
  governanceId,
  voterAddress,
  proposalId,
}) => {
  const { isContract, isLoading: isContractLoading } =
    useIsContract(voterAddress)

  const eligibleData = useAddressEligibleFreeVoting({
    governanceId,
    voterAddress,
    proposalId,
    accountType: isContract ? AccountType.Safe : AccountType.Eoa,
    enabled:
      Boolean(voterAddress) &&
      Boolean(governanceId) &&
      Boolean(proposalId) &&
      !isContractLoading,
  })

  const { values, setFieldValue } =
    useFormikContext<VotingModalSupportFormValues>()

  useEffect(() => {
    if (eligibleData?.sponsorshipSupported && eligibleData?.addressEligible) {
      // Only Eoa accounts are allowed to vote for free. This should be an edge case but catch it with sentry
      if (isContract) {
        captureMessage(
          `[VotingModalSupportForm] SAFE account type allowed to vote for free. address=${voterAddress} accountType=${AccountType.Safe}`,
        )
      }

      setFieldValue("hasFreeVoting", true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleData])

  if (!eligibleData?.sponsorshipSupported) {
    return null
  }

  return (
    <Stack align="flex-start" direction={{ base: "column", md: "row" }}>
      {eligibleData?.addressEligible ? (
        <>
          <Stack direction="column" maxWidth={{ base: "100%", md: "50%" }}>
            <Field name="hasFreeVoting">
              {({ field }: FieldProps) => (
                <FormControl>
                  <Checkbox {...field} isChecked={values.hasFreeVoting}>
                    <Text align="center" fontWeight="500" textStyle="sm">
                      Use free voting{" "}
                      <Badge colorScheme="purple" ml={1}>
                        <Text fontWeight="bold" textStyle="xs">
                          Beta
                        </Text>
                      </Badge>
                    </Text>
                  </Checkbox>
                </FormControl>
              )}
            </Field>
            <Text
              color="gray.500"
              textStyle="sm"
              width="100%"
            >{`Ledger doesn't support free voting. Free voting is available without comments.`}</Text>
          </Stack>
          <Spacer />
        </>
      ) : null}

      <VotingEligibleMessage
        addressEligible={eligibleData.addressEligible}
        displayLearnMore={false}
        justify="flex-end"
      />
    </Stack>
  )
}

type SupportRadioGroupProps = {
  governorType?: GovernorType
  customSupports?: SupportWithLabel[] | null
}

export const SupportRadioGroup: FC<SupportRadioGroupProps> = ({
  customSupports,
  governorType,
}) => {
  const { values, setFieldValue } =
    useFormikContext<VotingModalSupportFormValues>()
  const { support } = values
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "support",
    value: support,
    onChange: (value) => setFieldValue("support", value),
  })

  const radioGroup = getRootProps()

  const voteStyles = useMemo(() => {
    if (customSupports) {
      return getVoteStyles(customSupports.map((support) => support.value))
    }

    return getVoteStyles()
  }, [customSupports])

  const data: {
    key: string
    label: string
    voteStyle: VoteStyle
    radioProps: ReturnType<typeof getRadioProps>
  }[] = useMemo(() => {
    if (customSupports) {
      return customSupports.map((support) => ({
        key: `${support.value}_voting_radio`,
        label: support.label || capitalize(support.value),
        voteStyle: voteStyles[support.value],
        radioProps: getRadioProps({ value: support.value }),
      }))
    }

    const defaultSupportMap = (voteType: VoteType) => {
      return {
        key: `${voteType}_voting_radio`,
        label: capitalize(voteType),
        voteStyle: voteStyles[voteType],
        radioProps: getRadioProps({ value: voteType }),
      }
    }

    if (governorType === GovernorType.Governoralpha) {
      return [VoteType.For, VoteType.Against].map(defaultSupportMap)
    }

    return [VoteType.For, VoteType.Against, VoteType.Abstain].map(
      defaultSupportMap,
    )
  }, [customSupports, governorType, voteStyles, getRadioProps])

  return (
    <Stack {...radioGroup}>
      {data.map(({ key, label, voteStyle, radioProps }) => (
        <VoteCastingOption
          key={key}
          label={label}
          voteStyle={voteStyle}
          {...radioProps}
        >
          {label}
        </VoteCastingOption>
      ))}
    </Stack>
  )
}

type MultiChainVoteProps = {
  voter?: string
  votableChains: Delegation[]
  proposalId?: string
  onchainId?: string
  proposal: ProposalVoteModalQuery["proposal"]
}

const MultiChainVote = ({
  voter,
  votableChains,
  proposalId,
  onchainId,
  proposal,
}: MultiChainVoteProps) => {
  const [selected, setSelected] = useState<string[]>([])
  const [completedTransactions, setCompletedTransactions] = useState<
    CompletedTransaction[]
  >([])
  const { signer } = useSignerStore()

  const { data } = useProposalCastVotesQuery(
    {
      input: {
        filters: {
          voter: voter as string,
          proposalId: proposalId as string,
          includePendingVotes: true,
        },
        sort: {
          sortBy: VotesSortBy.Amount,
          isDescending: true,
        },
      },
    },
    { enabled: Boolean(proposalId && voter) },
  )

  const chainsToVoteOn = (): (Vote & Delegation)[] => {
    const combinedMap = new Map<string, Vote & Delegation>()

    const mergeObjects = (target: any, source: any) => {
      Object.keys(source).forEach((key) => {
        target[key] = source[key]
      })

      return target
    }

    const items = [
      ...(data?.votes.nodes.filter(
        (vote) => "chainId" in vote,
      ) as OnchainVote[]),
      ...votableChains,
    ]

    items.forEach((item) => {
      if (combinedMap.has(item.chainId)) {
        combinedMap.set(
          item.chainId,
          mergeObjects(combinedMap.get(item.chainId), item),
        )
      } else {
        combinedMap.set(item.chainId, item as Vote & Delegation)
      }
    })

    return Array.from(combinedMap.values())
  }

  function handleChainVoteClick(chainId?: Maybe<string>) {
    return () => {
      if (!chainId) {
        return
      }

      const alreadySelected = selected.includes(chainId)

      if (alreadySelected) {
        setSelected((chains) => chains.filter((c) => c !== chainId))

        return
      }

      setSelected((chains) => {
        const nextSelectedChains = chains.concat(chainId)

        return chains ? nextSelectedChains : [chainId]
      })
    }
  }

  function handleTransactionCompleted({
    governorId,
    transactionHash,
  }: CompletedTransaction) {
    setCompletedTransactions((completed) => {
      const alreadyCompleted = completed?.find(
        (completed) => completed.governorId === governorId,
      )

      if (!alreadyCompleted) {
        return completed.concat({ governorId, transactionHash })
      }

      return completed
    })
  }

  function isSubmitButtonEnabled(chain: [string, Chain] | undefined) {
    if (chain && chain?.[1].id.includes("solana")) {
      const proposalVotableChains = proposal?.votableChains?.filter(
        (chainId) => chainId && chainId.includes("solana"),
      )

      return proposalVotableChains && proposalVotableChains.length > 0
    }

    return true
  }

  return (
    <>
      <Stack border="1px solid" borderColor="gray.100" p={4} rounded="lg">
        <Stack>
          {(data?.votes?.nodes?.length ? chainsToVoteOn() : votableChains).map(
            (votableChain) => {
              const evmChain = Object.entries(chains).find(
                ([_, c]) =>
                  votableChain?.chainId &&
                  c.id === accountIdToChainId(votableChain?.chainId),
              )

              const solanaChain = getSolanaChain()

              const chain = evmChain || solanaChain

              const networkIcon = chain?.[1]
                ? GenericNetworkIcon(chain[1])
                : null

              const decimals = chain?.[1].id.includes("solana") ? 6 : 18

              const isChainSubmitButtonEnabled = isSubmitButtonEnabled(chain)

              return (
                <HStack key={votableChain.chainId} justifyItems="center">
                  <HStack flex={1}>
                    <IconButton
                      aria-label="Vote"
                      icon={
                        !selected.includes(votableChain?.chainId as string) ? (
                          <CircleDashed
                            _hover={{ color: "gray.700" }}
                            color="gray.400"
                            height={5}
                            width={5}
                          />
                        ) : (
                          <CircleCheckAlt
                            color="gray.700"
                            height={5}
                            width={5}
                          />
                        )
                      }
                      isDisabled={
                        data?.votes?.nodes?.length &&
                        (votableChain as OnchainVote & Delegation).txHash
                      }
                      variant="unstyled"
                      onClick={handleChainVoteClick(votableChain?.chainId)}
                    />
                    <Text color="gray.700" fontWeight="medium">
                      {getWeightLabel(
                        BigNumber.from(votableChain.votes),
                        decimals,
                      )}{" "}
                      votes
                    </Text>
                    <HStack
                      alignSelf="center"
                      bg="gray.100"
                      borderLeftRadius="full"
                      borderRightRadius="full"
                      px={2}
                    >
                      {networkIcon ? (
                        <Icon as={networkIcon} height={3} width={3} />
                      ) : null}
                      <Text color="gray.600" fontSize="sm" textAlign="center">
                        {chain?.[1]?.mediumName}
                      </Text>
                    </HStack>
                  </HStack>
                  <Flex>
                    {isChainSubmitButtonEnabled ? (
                      <MultiVoteSubmit
                        chainId={votableChain.chainId}
                        completedTransactions={completedTransactions}
                        isDisabled={!selected.includes(votableChain.chainId)}
                        onchainId={onchainId}
                        proposalId={proposalId}
                        signer={signer}
                        txHash={
                          data?.votes?.nodes?.length
                            ? (votableChain as OnchainVote & Delegation).txHash
                            : ""
                        }
                        voter={voter}
                        onTransactionComplete={handleTransactionCompleted}
                      />
                    ) : (
                      <Tooltip label="Vote is disabled since this proposal was not bridged yet">
                        <Button isDisabled size="sm" variant="primary" w="full">
                          Submit
                        </Button>
                      </Tooltip>
                    )}
                  </Flex>
                </HStack>
              )
            },
          )}
        </Stack>
      </Stack>
      <Stack backgroundColor="yellow.100" borderRadius={4} p={2}>
        <Text color="yellow.900" fontSize="sm" fontWeight="medium">
          Votes on non-mainnet chains will be automatically transferred
          cross-chain every ~15 minutes.
        </Text>
      </Stack>
    </>
  )
}

// type SolanaVoteProps = {
//   proposalId: string
// }

// type SupportedVoteTypes = Extract<
//   VoteType,
//   VoteType.For | VoteType.Against | VoteType.Abstain
// >

// const SolanaVote = ({ proposalId }: SolanaVoteProps) => {
//   const { values } = useFormikContext<VotingModalSupportFormValues>()
//   const { support } = values
//   const { stakeAccount, stakeAccountCustodyAddress, castVote } =
//     useStakeAccount()
//   const { connection } = useConnection()
//   const stakeAccountDisclosure = useDisclosure()

//   const [custodyBalance, setCustodyBalance] = useState("")

//   async function handleVote() {
//     if (!stakeAccount) {
//       stakeAccountDisclosure.onOpen()
//     }

//     if (!custodyBalance) return

//     const voteTypeMap: Record<string, VoteArgs> = {
//       [VoteType.For]: [new BN(0), new BN(custodyBalance), new BN(0)],
//       [VoteType.Against]: [new BN(custodyBalance), new BN(0), new BN(0)],
//       [VoteType.Abstain]: [new BN(0), new BN(0), new BN(custodyBalance)],
//     }

//     try {
//       if (!isSupportedVoteType(support)) {
//         throw new Error("Unknown vote type")
//       }

//       const result = await castVote(proposalId, ...voteTypeMap[support])

//       return result
//     } catch (err) {
//       console.error("u messed up kid", err)
//     }
//   }

//   function isSupportedVoteType(vote: string): vote is SupportedVoteTypes {
//     return [VoteType.For, VoteType.Against, VoteType.Abstain].includes(
//       vote as VoteType,
//     )
//   }

//   useEffect(() => {
//     async function getCustodyDetails() {
//       if (!stakeAccountCustodyAddress) {
//         return
//       }

//       try {
//         const custody = await getAccount(connection, stakeAccountCustodyAddress)

//         const wh = new WHTokenBalance(new BN(custody.amount.toString()))

//         setCustodyBalance(wh.toString())
//       } catch {
//         setCustodyBalance("")
//       }
//     }

//     getCustodyDetails()
//   }, [stakeAccountCustodyAddress, connection])

//   return (
//     <Stack>
//       <Text>{custodyBalance ?? "--"} W available to vote with</Text>
//       <Button
//         isDisabled={!custodyBalance || !stakeAccountCustodyAddress || !support}
//         variant="primary"
//         onClick={handleVote}
//       >
//         Submit vote
//       </Button>
//       <StakeAccountIntercept
//         voting
//         isOpen={stakeAccountDisclosure.isOpen}
//         onClose={stakeAccountDisclosure.onClose}
//       />
//     </Stack>
//   )
// }

type WormholeVoteArgs = [
  InstanceType<typeof BN>,
  InstanceType<typeof BN>,
  InstanceType<typeof BN>,
] // against, for, abstain

type CompletedTransaction = {
  governorId: string
  transactionHash: string
}

type MultiVoteSubmitProps = {
  isDisabled: boolean
  signer?: FetchSignerResult<Signer>
  onchainId?: string
  proposalId?: string
  chainId: string
  txHash?: string
  voter?: string
  completedTransactions: CompletedTransaction[]
  onTransactionComplete: (tx: CompletedTransaction) => void
}
const MultiVoteSubmit = ({
  completedTransactions,
  isDisabled,
  signer,
  onchainId,
  proposalId,
  chainId,
  voter,
  txHash = "",
  onTransactionComplete,
}: MultiVoteSubmitProps) => {
  const chainReference = useChainReference()
  const { castVote, status, governorId } = useCastVote({
    chainReference,
    isMultiChain: true,
  })
  const { castVoteBySig } = useCastVoteBySig({
    chainReference,
    isMultiChain: true,
  })

  const {
    stakeAccount,
    castVote: castSolanaVote,
    transactionStatus,
  } = useStakeAccount()
  const stakeAccountDisclosure = useDisclosure()

  const { governors } = useOrganization()
  const governorToVoteOn = governors?.find((gov) => gov.chainId === chainId)

  const { values } = useFormikContext<VotingModalSupportFormValues>()
  const { support, reason, hasFreeVoting } = values

  function vote() {
    if (!governorToVoteOn || !proposalId || !onchainId) {
      return
    }

    if (hasFreeVoting) {
      castVoteBySig({
        onchainId,
        proposalId,
        support: support as VoteType,
        reason,
        signer,
        governance: governorToVoteOn,
      })

      return
    }

    castVote({
      proposalId,
      onchainId,
      type: support as VoteType,
      reason,
      signer,
      governance: governorToVoteOn,
    })
  }

  const { data: solanaVotingPowerData } = useProposalVoteModalQuery(
    {
      input: {
        id: proposalId,
      },
      accountId:
        voter && isSolanaAddress(voter)
          ? `${getSolanaChain()?.[1].id}:${voter}`
          : "",
      address: voter ?? "",
    },
    {
      enabled: voter !== undefined && isSolanaAddress(voter),
    },
  )

  const solanaVotingPower = useMemo(() => {
    if (!solanaVotingPowerData) return

    return solanaVotingPowerData.proposal.delegateVotesCount ?? "0"
  }, [solanaVotingPowerData])

  async function solanaVote() {
    try {
      if (!stakeAccount) {
        stakeAccountDisclosure.onOpen()

        return
      }

      if (!solanaVotingPower) {
        return
      }

      if (!onchainId) {
        return
      }

      const voteTypeMap: Record<string, WormholeVoteArgs> = {
        [VoteType.For]: [new BN(0), new BN(solanaVotingPower), new BN(0)],
        [VoteType.Against]: [new BN(solanaVotingPower), new BN(0), new BN(0)],
        [VoteType.Abstain]: [new BN(0), new BN(0), new BN(solanaVotingPower)],
      }

      return castSolanaVote(onchainId, ...voteTypeMap[support])
    } catch (error) {
      if (error instanceof Error) {
        Sentry.captureException(
          new Error(toFormattedString(error)),
          (scope) => {
            scope.setLevel(Severity.Error)
            scope.setContext("solanaVotingPower", { solanaVotingPower })

            return scope
          },
        )
      }
    }
  }

  const button = () => {
    if (txHash) {
      const chainReference = chainIdToChainReference(chainId)

      return (
        <Link href={EXTERNAL_ROUTES.etherscan.hash(chainReference, txHash)}>
          <Text color="gray.500" fontSize="sm">
            Complete
            <ArrowUpRight color="gray.500" height={3} ml={1} width={3} />
          </Text>
        </Link>
      )
    }

    if (
      (status.state === TransactionState.Mining &&
        status?.metadata?.governanceId === governorId) ||
      transactionStatus === TransactionStatus.CAST_VOTE
    ) {
      return (
        <Button isDisabled size="sm" variant="primary" w="full">
          Waiting...
        </Button>
      )
    }

    if (status.state === TransactionState.Success) {
      onTransactionComplete({
        governorId: status.metadata.governanceId,
        transactionHash: status.receipt.transactionHash,
      })
    }

    if (
      Boolean(
        completedTransactions.find(
          (c) => c.governorId === governorToVoteOn?.id,
        ),
      )
    ) {
      const completed = completedTransactions.find(
        (c) => c.governorId === governorToVoteOn?.id,
      )
      const chainReference = chainIdToChainReference(chainId)

      if (completed) {
        return (
          <Link
            href={EXTERNAL_ROUTES.etherscan.hash(
              chainReference,
              completed.transactionHash,
            )}
          >
            <Text color="gray.500" fontSize="sm">
              Complete
              <ArrowUpRight color="gray.500" height={3} ml={1} width={3} />
            </Text>
          </Link>
        )
      }

      return (
        <Text color="gray.500" fontSize="sm">
          Complete
          <ArrowUpRight color="gray.500" height={3} ml={1} width={3} />
        </Text>
      )
    }

    return !chainId.includes("solana") ? (
      <Web3Button
        isMultiChain
        isDisabled={isDisabled || !support}
        requiredChainId={chainId}
        size="sm"
        variant="primary"
        w="full"
        onValid={vote}
      >
        Submit
      </Web3Button>
    ) : (
      <>
        <Button
          isDisabled={isDisabled || !support}
          size="sm"
          variant="primary"
          w="full"
          onClick={solanaVote}
        >
          Submit
        </Button>
        <StakeAccountIntercept
          voting
          isOpen={stakeAccountDisclosure.isOpen}
          onClose={stakeAccountDisclosure.onClose}
        />
      </>
    )
  }

  return button()
}
